"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.savedConfigDocumentSchema = exports.grantWithoutGroupSchema = exports.configRebuildStateSchema = exports.REBUILD_STATES = exports.rebuildStopReasonSchema = exports.configRebuildSchema = exports.CONFIG_REBUILD_STATUSES = exports.configDocumentSchema = exports.updateConfigDocumentBodySchema = exports.configDocumentParamsSchema = exports.configDocumentKeySchema = exports.CONFIG_DOCUMENT_KEYS = void 0;
// Wire shapes of the Settings screen's configuration documents (SPEC §9 `GET/PUT /api/config/:key` and
// `POST /api/config/export`, §14.1, §14.6; CR-001 §8; J-042), served under `/api/v1` as
// `/config-documents/:key` and `/config-exports` (D-973). A document is stored as written in
// `ref.app_config` (D-1052): `organization` and `ui` are one object each; `measures`, `benchmarks` and
// `synthetic` are maps of file stem to document, as `config/<folder>/<stem>.yaml` holds them.
const zod_1 = require("zod");
const bundles_js_1 = require("./bundles.js");
/** The five configuration documents, in the order `config:export` writes them (SPEC §14.1). */
exports.CONFIG_DOCUMENT_KEYS = [
    "organization",
    "measures",
    "benchmarks",
    "synthetic",
    "ui",
];
exports.configDocumentKeySchema = zod_1.z.enum(exports.CONFIG_DOCUMENT_KEYS);
// No `.meta({ id })` on a params schema: @fastify/swagger expands parameters inline.
exports.configDocumentParamsSchema = zod_1.z.object({ key: exports.configDocumentKeySchema });
/** Every document is a YAML mapping, so its JSON form is an object; its own schema runs on save. */
const documentBodySchema = zod_1.z.record(zod_1.z.string(), zod_1.z.unknown());
exports.updateConfigDocumentBodySchema = zod_1.z.object({
    /** The version the editor read (SPEC §14.1); a save against any other version gets 409. */
    version: zod_1.z.number().int().min(1),
    document: documentBodySchema,
});
exports.configDocumentSchema = zod_1.z
    .object({
    key: exports.configDocumentKeySchema,
    /** Counts saves: `config:apply` bumps it when the content changes, the Settings screen on every save. */
    version: zod_1.z.number().int().min(1),
    updatedAt: zod_1.z.string(),
    /** The saving user's opaque id, or `config:apply` for the command line. */
    updatedBy: zod_1.z.string().nullable(),
    document: documentBodySchema,
})
    .meta({ id: "AcoConfigDocument" });
/**
 * What a save did about the results (J-210, inbox 06 PR E point 6; J-297 QB4 item 1c). A save of the
 * organization, measures or benchmarks queues a rebuild of every year with a loaded bundle, in the
 * save's own transaction, and answers at once; the bundle loop runs it, and `GET /config-rebuilds`
 * reads its progress and result:
 *   - `queued`      the rebuild is queued (also the answer of `POST /config-rebuilds`, the retry);
 *   - `not_needed`  the document does not feed the results (`synthetic`, `ui`).
 * Before J-297 the save rebuilt inside the request and also answered `rebuilt` or `failed`; a rebuild
 * slower than the request's time limit then turned a committed save into "Not saved".
 */
exports.CONFIG_REBUILD_STATUSES = ["queued", "not_needed"];
exports.configRebuildSchema = zod_1.z
    .object({
    status: zod_1.z.enum(exports.CONFIG_REBUILD_STATUSES),
    /** Always empty: the years are on the rebuild's state once it has run. */
    years: zod_1.z.array(zod_1.z.number().int()),
})
    .meta({ id: "AcoConfigRebuild" });
/**
 * J-297 (QB4 item 1c): why the rebuild after a save stopped before it finished, in the shape of a
 * bundle's stop (`bundleStopReasonSchema`). `step` is the year it was rebuilding.
 */
exports.rebuildStopReasonSchema = bundles_js_1.bundleStopReasonSchema
    .extend({ work: zod_1.z.enum(["rebuild"]) })
    .meta({ id: "AcoConfigRebuildStopReason" });
/**
 * The rebuild's state, kept in `ref.app_config` `calculation_status.rebuild` (J-297):
 *   - `idle`     no save has queued a rebuild yet;
 *   - `queued`   a save or a retry queued it, or a stop handed it back once; the bundle loop starts it
 *                within its 2-second tick once no load, calculation or reset holds the lock;
 *   - `running`  the bundle loop is rebuilding; `beatAt` moves at least every 30 seconds;
 *   - `rebuilt`  done: `years` were rebuilt with the newest saved settings;
 *   - `failed`   stopped: `error` is a code (`REBUILD_INTERRUPTED` after a second stop), never a
 *                message or a value; the results still come from earlier settings until a retry.
 */
exports.REBUILD_STATES = ["idle", "queued", "running", "rebuilt", "failed"];
exports.configRebuildStateSchema = zod_1.z
    .object({
    state: zod_1.z.enum(exports.REBUILD_STATES),
    /** ISO 8601 times; null until the step happened. */
    queuedAt: zod_1.z.string().nullable(),
    startedAt: zod_1.z.string().nullable(),
    finishedAt: zod_1.z.string().nullable(),
    beatAt: zod_1.z.string().nullable(),
    /** The years the run covers, known once it starts; the years rebuilt once `rebuilt`. */
    years: zod_1.z.array(zod_1.z.number().int()),
    /** The years the run in hand has already rebuilt: its progress. */
    yearsDone: zod_1.z.array(zod_1.z.number().int()),
    /** The replica or job execution that ran it last. */
    worker: zod_1.z.string().nullable(),
    /** The process's resident memory high-water mark in MiB during the last run, sampled at each beat. */
    peakRssMb: zod_1.z.number().int().nullable(),
    /** A code, never a message or a value; null unless `failed`. */
    error: zod_1.z.string().nullable(),
    /** The last stop, kept while the rebuild starts again and after it ends. */
    stopReason: exports.rebuildStopReasonSchema.nullable(),
})
    .meta({ id: "AcoConfigRebuildState" });
/**
 * J-268 (QB3 item 1): a performance-group grant whose group the saved organization no longer has. The
 * grant is kept and reaches nothing; a group of that name in a later organization takes it back.
 */
exports.grantWithoutGroupSchema = zod_1.z
    .object({ email: zod_1.z.string(), performanceYear: zod_1.z.number().int(), groupName: zod_1.z.string() })
    .meta({ id: "AcoGrantWithoutGroup" });
/** `PUT /config-documents/:key`: the saved document and what happened to the results. */
exports.savedConfigDocumentSchema = exports.configDocumentSchema
    .extend({
    rebuild: exports.configRebuildSchema,
    /** J-268: grants naming a group that no longer exists, after this save; empty when none. */
    grantsWithoutGroup: zod_1.z.array(exports.grantWithoutGroupSchema),
})
    .meta({ id: "AcoSavedConfigDocument" });
//# sourceMappingURL=config-documents.js.map